import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../utils/api';

interface User {
  id: number;
  username: string;
  role: string;
  shopId?: number;
  shopName?: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const isInitialized = useRef(false);
  const isTokenValidating = useRef(false);

  const validateToken = useCallback(async (token: string) => {
    // Защита от повторных вызовов
    if (isTokenValidating.current) {
      return;
    }
    
    // Проверяем, что токен не пустой
    if (!token || token.trim() === '') {
      setLoading(false);
      return;
    }
    
    isTokenValidating.current = true;
    
    try {
      const response = await api.get('/auth/validate');
      const userData = response.data.user;
      
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
    } catch (error: any) {
      console.error('Token validation failed:', error);
      
      // Если ошибка 401 или 403, очищаем данные
      if (error.response?.status === 401 || error.response?.status === 403) {
        logout();
      } else if (error.code === 'NETWORK_ERROR' || error.message?.includes('Network Error')) {
        // Если ошибка сети, не выходим из системы, пытаемся восстановить пользователя
        const savedUser = localStorage.getItem('user');
        if (savedUser) {
          try {
            const parsedUser = JSON.parse(savedUser);
            setUser(parsedUser);
          } catch (e) {
            console.error('Failed to parse saved user:', e);
          }
        }
      } else {
        // Для других ошибок также очищаем данные
        logout();
      }
    } finally {
      isTokenValidating.current = false;
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setLoading(false);
  }, []);

  useEffect(() => {
    // Защита от повторной инициализации
    if (isInitialized.current) {
      return;
    }
    
    isInitialized.current = true;
    
    // Проверяем сохраненный токен при загрузке (без "ложной авторизации")
    const token = localStorage.getItem('token');
    if (token && token.trim() !== '') {
      validateToken(token);
    } else {
      // Без валидного токена пользователь не считается авторизованным
      setUser(null);
      setLoading(false);
    }

    // Устанавливаем интервал для проверки токена каждые 30 минут
    const tokenCheckInterval = setInterval(() => {
      const currentToken = localStorage.getItem('token');
      if (currentToken && currentToken.trim() !== '' && !isTokenValidating.current) {
        validateToken(currentToken);
      }
    }, 30 * 60 * 1000); // 30 минут

    return () => {
      clearInterval(tokenCheckInterval);
    };
  }, []); // Убираем зависимость от validateToken, чтобы избежать циклов

  // Реакция на мягкую 401-авторизацию из api-интерсептора
  useEffect(() => {
    const onUnauthorized = () => logout();
    window.addEventListener('auth:unauthorized', onUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', onUnauthorized);
  }, [logout]);

  const login = async (username: string, password: string) => {
    try {
      const response = await api.post('/auth/login', { username, password });
      const { token, user: userData } = response.data;
      
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userData));
      
      setUser(userData);
    } catch (error) {
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    login,
    logout,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
